// Arrange mode (F-STN-19, spec/13 §3.4): the editor that makes the live floor look like the room. A grid of up to
// 60 × 100 squares, one plan per floor (levels 0–9), with three layers edited one at a time:
//
//   - Stations: drag a tile (or a tray entry) onto a square; dropping on another station swaps them. The keyboard
//     does the same — select a tile, arrow keys move it one square (swapping), Delete sends it back to the tray;
//     a tray entry placed with Enter takes the first free square. Auto-layout arranges every station in number order.
//   - Zones: pick one of the venue's zones for this floor and paint its rectangle by dragging across the grid; arrow
//     keys move a drawn zone, Shift + arrows resize it.
//   - Fixtures: counter, entrance, lounge, wall, pillar, screen, window, restroom, stairs — added, labelled, moved
//     and resized the same way.
//
// Floor-size steppers refuse to cut through placed items (`resizeRefusal`), and the plan is checked with the exact
// rules the server runs (`floorPlanProblems`, `@clavian/domain`) before Save is offered. Save sends the version it
// was based on; a `412` means another manager saved first and the editor offers their plan. Every other console
// hears `floor.plan.updated` and re-reads the floor. Save and Cancel are explicit.

import type { FixtureKind, FloorFixture, FloorPlan, FloorStation, FloorZone, PutFloorPlan, Zone } from '@clavian/api-client';
import { FIXTURE_KINDS, FLOOR_MAX, FLOOR_MAX_COLUMNS, FLOOR_MAX_ROWS, FLOOR_MIN, MAX_FIXTURES_PER_FLOOR } from '@clavian/contracts/constants';
import {
  autoLayoutStations,
  dropStation,
  floorPlanProblems,
  nudgeStation,
  resizeRefusal,
  trayStation,
  type PlacedStation,
} from '@clavian/domain/floor';
import { useTranslation } from '@clavian/i18n/react';
import { Link } from '@tanstack/react-router';
import { Badge, Button, cx, SegmentedControl, Select, useToast } from '@clavian/ui-web';
import { useEffect, useMemo, useRef, useState, type CSSProperties, type DragEvent, type KeyboardEvent, type PointerEvent, type ReactNode } from 'react';

import { useAccess } from '../api/queries';
import { isStaleWrite, PROBLEM, problemCode } from '../api/problems';
import { useProblem } from '../api/use-problem';
import { Loading } from '../shell/layouts';
import { LoadFailure } from '../shell/LoadFailure';
import { Icon } from '../shell/nav-icons';
import { useCurrentVenue } from '../shell/scope';
import { tokenColor } from '../stations/fields';
import { useZones } from '../stations/queries';
import { Forbidden, Notice, Page, Toolbar, ToolbarSpring } from '../ui';
import { floorKeys, useFloor, useFloorPlan, useSaveFloorPlan } from './queries';

import './arrange.css';

const FLOOR_ARRANGE = 'floor:arrange';
const STATION_DRAG_TYPE = 'application/x-clavian-station';
const FIXTURE_DRAG_TYPE = 'application/x-clavian-fixture';

type Layer = 'stations' | 'zones' | 'fixtures';
type Selection =
  | { readonly kind: 'station'; readonly stationId: string }
  | { readonly kind: 'zone'; readonly zoneId: string }
  | { readonly kind: 'fixture'; readonly index: number }
  | null;

interface Draft {
  readonly columns: number;
  readonly rows: number;
  readonly zones: readonly FloorZone[];
  readonly fixtures: readonly FloorFixture[];
  readonly stations: readonly FloorStation[];
}

function draftOf(plan: FloorPlan): Draft {
  return { columns: plan.columns, rows: plan.rows, zones: plan.zones, fixtures: plan.fixtures, stations: plan.stations };
}

function sameDraft(a: Draft, b: Draft): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

/** An item's place on the CSS grid: 1-based lines. */
function gridPlace(rect: { readonly x: number; readonly y: number; readonly w: number; readonly h: number }): CSSProperties {
  return { gridColumn: `${rect.x + 1} / span ${rect.w}`, gridRow: `${rect.y + 1} / span ${rect.h}` };
}

/** Arrow key → one square. */
function arrowDelta(key: string): readonly [number, number] | undefined {
  switch (key) {
    case 'ArrowLeft':
      return [-1, 0];
    case 'ArrowRight':
      return [1, 0];
    case 'ArrowUp':
      return [0, -1];
    case 'ArrowDown':
      return [0, 1];
    default:
      return undefined;
  }
}

/** Move (or with `resize`, grow/shrink) a rectangle by one square, kept inside the grid. */
function stepRect<T extends { x: number; y: number; w: number; h: number }>(rect: T, dx: number, dy: number, resize: boolean, columns: number, rows: number): T {
  if (resize) {
    const w = clamp(rect.w + dx, 1, columns - rect.x);
    const h = clamp(rect.h + dy, 1, rows - rect.y);
    return { ...rect, w, h };
  }
  return { ...rect, x: clamp(rect.x + dx, 0, Math.max(columns - rect.w, 0)), y: clamp(rect.y + dy, 0, Math.max(rows - rect.h, 0)) };
}

export function FloorArrangePage(): ReactNode {
  const { t } = useTranslation('console');
  const venue = useCurrentVenue();
  const access = useAccess({ venueId: venue.id });
  const [floor, setFloor] = useState(FLOOR_MIN);
  const plan = useFloorPlan(venue.organizationId, venue.id, floor);
  const snapshot = useFloor(venue.organizationId, venue.id);
  const zones = useZones(venue.organizationId, venue.id);

  if (access.status === 'ready' && !access.can(FLOOR_ARRANGE)) {
    return (
      <Page title={t('floor.arrange.title')}>
        <Forbidden message={t('floor.arrange.notPermitted')} />
      </Page>
    );
  }
  if (plan.data === undefined) {
    if (plan.isError && problemCode(plan.error) === PROBLEM.forbidden) {
      return (
        <Page title={t('floor.arrange.title')}>
          <Forbidden message={t('floor.arrange.notPermitted')} />
        </Page>
      );
    }
    return plan.isError ? <LoadFailure error={plan.error} message={t('floor.arrange.unavailable')} onRetry={() => void plan.refetch()} /> : <Loading />;
  }

  return (
    <ArrangeEditor
      key={`${floor}`}
      floor={floor}
      onFloorChange={setFloor}
      plan={plan.data}
      stations={snapshot.data?.stations ?? []}
      // Only the zones on this floor can be drawn here (a zone's floor is fixed, spec/13 §3.4).
      zones={(zones.data ?? []).filter((zone) => zone.floor === floor)}
      // The zones query is a separate request from the plan: pass its own loading and error state so the
      // Zones layer can tell "still loading / failed to load" apart from "this floor has no zones". Without
      // this, a pending or failed `useZones` collapses to `[]` and the panel wrongly says the floor is empty.
      zonesLoading={zones.data === undefined && !zones.isError}
      zonesError={zones.isError}
      onReloadZones={() => void zones.refetch()}
      onReload={() => void plan.refetch()}
    />
  );
}

interface ArrangeStation {
  readonly stationId: string;
  readonly number: number;
  readonly name: string | null;
}

function ArrangeEditor({
  floor,
  onFloorChange,
  plan,
  stations,
  zones,
  zonesLoading,
  zonesError,
  onReloadZones,
  onReload,
}: {
  readonly floor: number;
  readonly onFloorChange: (floor: number) => void;
  readonly plan: FloorPlan;
  readonly stations: readonly ArrangeStation[];
  readonly zones: readonly Zone[];
  readonly zonesLoading: boolean;
  readonly zonesError: boolean;
  readonly onReloadZones: () => void;
  readonly onReload: () => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const venue = useCurrentVenue();
  const { show } = useToast();
  const save = useSaveFloorPlan(venue.organizationId, venue.id);

  const [base, setBase] = useState<FloorPlan>(plan);
  const [draft, setDraft] = useState<Draft>(() => draftOf(plan));
  const [layer, setLayer] = useState<Layer>('stations');
  const [selection, setSelection] = useState<Selection>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [stale, setStale] = useState(false);
  const [newFixture, setNewFixture] = useState<FixtureKind>('counter');
  const [paint, setPaint] = useState<{ readonly x0: number; readonly y0: number; readonly x1: number; readonly y1: number } | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef(new Map<string, HTMLElement>());

  const dirty = !sameDraft(draft, draftOf(base));

  // A newer plan arrived (another console saved and `floor.plan.updated` re-read it): adopt it when nothing is
  // pending here, otherwise say so and let the manager choose. Compared during render (React's "adjust state when a
  // prop changes"), so the adoption lands in the same pass rather than a cascading effect.
  const [seenVersion, setSeenVersion] = useState(plan.version);
  if (plan.version !== seenVersion) {
    setSeenVersion(plan.version);
    if (!dirty) {
      setBase(plan);
      setDraft(draftOf(plan));
      setStale(false);
    } else if (plan.version > base.version) {
      setStale(true);
    }
  }

  // Keep keyboard focus on the item being moved.
  useEffect(() => {
    if (selection === null) return;
    const key = selection.kind === 'station' ? `s:${selection.stationId}` : selection.kind === 'zone' ? `z:${selection.zoneId}` : `f:${selection.index}`;
    itemRefs.current.get(key)?.focus();
  }, [selection, draft]);

  const byId = useMemo(() => new Map(stations.map((station) => [station.stationId, station])), [stations]);
  const zoneById = useMemo(() => new Map(zones.map((zone) => [zone.id, zone])), [zones]);
  const elsewhere = useMemo(() => new Set(plan.stationsElsewhere.map((entry) => entry.stationId)), [plan.stationsElsewhere]);
  const placed = useMemo(() => new Set(draft.stations.map((station) => station.stationId)), [draft.stations]);
  const tray = useMemo(
    () => [...stations].filter((station) => !placed.has(station.stationId) && !elsewhere.has(station.stationId)).sort((a, b) => a.number - b.number),
    [stations, placed, elsewhere],
  );
  const problems = useMemo(() => floorPlanProblems(draft), [draft]);
  const stationName = (stationId: string) => {
    const station = byId.get(stationId);
    return station === undefined ? t('floor.arrange.unknownStation') : (station.name ?? t('floor.tile.numbered', { number: station.number }));
  };

  const update = (next: Partial<Draft>) => {
    setRefusal(null);
    setDraft((current) => ({ ...current, ...next }));
  };
  const setStations = (next: readonly PlacedStation[]) => update({ stations: next.map((station) => ({ ...station, stationId: station.stationId as FloorStation['stationId'] })) });

  // --- geometry --------------------------------------------------------------------------------

  const cellAt = (clientX: number, clientY: number): { readonly x: number; readonly y: number } | undefined => {
    const grid = gridRef.current;
    if (grid === null) return undefined;
    const rect = grid.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return undefined;
    const x = Math.floor(((clientX - rect.left) / rect.width) * draft.columns);
    const y = Math.floor(((clientY - rect.top) / rect.height) * draft.rows);
    if (x < 0 || y < 0 || x >= draft.columns || y >= draft.rows) return undefined;
    return { x, y };
  };

  const firstFreeSquare = (): { readonly x: number; readonly y: number } | undefined => {
    const [spot] = autoLayoutStations(['probe'], draft.columns, draft.rows, [...draft.fixtures, ...draft.stations]);
    return spot === undefined ? undefined : { x: spot.x, y: spot.y };
  };

  // --- stations ------------------------------------------------------------------------------

  const placeStation = (stationId: string, x: number, y: number) => {
    setStations(dropStation(draft.stations, stationId, x, y));
    setSelection({ kind: 'station', stationId });
  };

  const placeFromTray = (stationId: string) => {
    const spot = firstFreeSquare();
    if (spot === undefined) {
      setRefusal(t('floor.arrange.noRoom'));
      return;
    }
    placeStation(stationId, spot.x, spot.y);
  };

  const onDragStart = (event: DragEvent, stationId: string) => {
    event.dataTransfer.setData(STATION_DRAG_TYPE, stationId);
    event.dataTransfer.setData('text/plain', stationId);
    event.dataTransfer.effectAllowed = 'move';
  };

  const onDrop = (event: DragEvent) => {
    const cell = cellAt(event.clientX, event.clientY);
    if (cell === undefined) return;
    // A fixture carries its index; a station carries its id (or the plain-text fallback a native drag adds).
    const fixtureIndex = event.dataTransfer.getData(FIXTURE_DRAG_TYPE);
    if (fixtureIndex !== '') {
      const index = Number(fixtureIndex);
      if (Number.isInteger(index) && index >= 0 && index < draft.fixtures.length) {
        event.preventDefault();
        placeFixture(index, cell.x, cell.y);
      }
      return;
    }
    const stationId = event.dataTransfer.getData(STATION_DRAG_TYPE) || event.dataTransfer.getData('text/plain');
    if (stationId === '' || !byId.has(stationId)) return;
    event.preventDefault();
    placeStation(stationId, cell.x, cell.y);
  };

  const onStationKey = (event: KeyboardEvent, stationId: string) => {
    const delta = arrowDelta(event.key);
    if (delta !== undefined) {
      event.preventDefault();
      setStations(nudgeStation(draft.stations, stationId, delta[0], delta[1], draft.columns, draft.rows));
      return;
    }
    if (event.key === 'Delete' || event.key === 'Backspace') {
      event.preventDefault();
      setStations(trayStation(draft.stations, stationId));
      setSelection(null);
    }
  };

  const autoLayout = () => {
    const ordered = [...stations].filter((station) => !elsewhere.has(station.stationId)).sort((a, b) => a.number - b.number).map((station) => station.stationId);
    const laid = autoLayoutStations(ordered, draft.columns, draft.rows, draft.fixtures);
    setStations(laid);
    if (laid.length < ordered.length) setRefusal(t('floor.arrange.autoLayoutOverflow', { count: ordered.length - laid.length }));
  };

  // --- zones ---------------------------------------------------------------------------------

  const selectedZoneId = selection?.kind === 'zone' ? selection.zoneId : null;

  const onGridPointerDown = (event: PointerEvent) => {
    if (layer !== 'zones' || selectedZoneId === null || event.button !== 0) return;
    if (event.target !== gridRef.current && !(event.target as HTMLElement).classList.contains('console-arrange__zone--selected')) return;
    const cell = cellAt(event.clientX, event.clientY);
    if (cell === undefined) return;
    (event.currentTarget as HTMLElement).setPointerCapture?.(event.pointerId);
    setPaint({ x0: cell.x, y0: cell.y, x1: cell.x, y1: cell.y });
  };

  const onGridPointerMove = (event: PointerEvent) => {
    if (paint === null) return;
    const cell = cellAt(event.clientX, event.clientY);
    if (cell !== undefined) setPaint({ ...paint, x1: cell.x, y1: cell.y });
  };

  const onGridPointerUp = () => {
    if (paint === null || selectedZoneId === null) return;
    const rect = { x: Math.min(paint.x0, paint.x1), y: Math.min(paint.y0, paint.y1), w: Math.abs(paint.x1 - paint.x0) + 1, h: Math.abs(paint.y1 - paint.y0) + 1 };
    setPaint(null);
    const others = draft.zones.filter((zone) => zone.zoneId !== selectedZoneId);
    update({ zones: [...others, { zoneId: selectedZoneId as FloorZone['zoneId'], ...rect }] });
  };

  const drawZone = (zoneId: string) => {
    if (draft.zones.some((zone) => zone.zoneId === zoneId)) {
      setSelection({ kind: 'zone', zoneId });
      return;
    }
    const w = Math.min(4, draft.columns);
    const h = Math.min(3, draft.rows);
    update({ zones: [...draft.zones, { zoneId: zoneId as FloorZone['zoneId'], x: 0, y: 0, w, h }] });
    setSelection({ kind: 'zone', zoneId });
  };

  const onZoneKey = (event: KeyboardEvent, zoneId: string) => {
    const delta = arrowDelta(event.key);
    if (delta !== undefined) {
      event.preventDefault();
      update({ zones: draft.zones.map((zone) => (zone.zoneId === zoneId ? stepRect({ ...zone }, delta[0], delta[1], event.shiftKey, draft.columns, draft.rows) : zone)) });
      return;
    }
    if (event.key === 'Delete' || event.key === 'Backspace') {
      event.preventDefault();
      update({ zones: draft.zones.filter((zone) => zone.zoneId !== zoneId) });
      setSelection(null);
    }
  };

  // --- fixtures ------------------------------------------------------------------------------

  const addFixture = () => {
    if (draft.fixtures.length >= MAX_FIXTURES_PER_FLOOR) {
      setRefusal(t('floor.arrange.tooManyFixtures', { max: MAX_FIXTURES_PER_FLOOR }));
      return;
    }
    const w = Math.min(newFixture === 'wall' || newFixture === 'counter' ? 3 : 1, draft.columns);
    update({ fixtures: [...draft.fixtures, { kind: newFixture, label: null, x: 0, y: 0, w, h: 1 }] });
    setSelection({ kind: 'fixture', index: draft.fixtures.length });
  };

  // Drag a fixture to a square, mirroring the station drag (F-STN-19): dropping moves its top-left corner
  // to the cell, kept wholly inside the grid. The keyboard path (arrow keys, Shift to resize) stays.
  const placeFixture = (index: number, x: number, y: number) => {
    update({
      fixtures: draft.fixtures.map((fixture, at) =>
        at === index ? { ...fixture, x: clamp(x, 0, Math.max(draft.columns - fixture.w, 0)), y: clamp(y, 0, Math.max(draft.rows - fixture.h, 0)) } : fixture,
      ),
    });
    setSelection({ kind: 'fixture', index });
  };

  const onFixtureDragStart = (event: DragEvent, index: number) => {
    event.dataTransfer.setData(FIXTURE_DRAG_TYPE, String(index));
    event.dataTransfer.effectAllowed = 'move';
  };

  const onFixtureKey = (event: KeyboardEvent, index: number) => {
    const delta = arrowDelta(event.key);
    if (delta !== undefined) {
      event.preventDefault();
      update({ fixtures: draft.fixtures.map((fixture, at) => (at === index ? stepRect({ ...fixture }, delta[0], delta[1], event.shiftKey, draft.columns, draft.rows) : fixture)) });
      return;
    }
    if (event.key === 'Delete' || event.key === 'Backspace') {
      event.preventDefault();
      update({ fixtures: draft.fixtures.filter((_, at) => at !== index) });
      setSelection(null);
    }
  };

  const setFixtureLabel = (index: number, label: string) =>
    update({ fixtures: draft.fixtures.map((fixture, at) => (at === index ? { ...fixture, label: label.trim() === '' ? null : label.slice(0, 40) } : fixture)) });

  // --- size, save, cancel --------------------------------------------------------------------

  const resize = (columns: number, rows: number) => {
    const nextColumns = clamp(columns, 1, FLOOR_MAX_COLUMNS);
    const nextRows = clamp(rows, 1, FLOOR_MAX_ROWS);
    const refused = resizeRefusal(draft, nextColumns, nextRows);
    if (refused !== undefined) {
      setRefusal(refused);
      return;
    }
    update({ columns: nextColumns, rows: nextRows });
  };

  const body = (): PutFloorPlan => ({ columns: draft.columns, rows: draft.rows, zones: [...draft.zones], fixtures: [...draft.fixtures], stations: [...draft.stations] });

  const onSave = () => {
    save.mutate(
      { floor, baseVersion: base.version, body: body() },
      {
        onSuccess: (saved) => {
          setBase(saved);
          setDraft(draftOf(saved));
          setStale(false);
          show({ tone: 'ok', title: t('floor.arrange.saved') });
        },
        onError: (error) => {
          if (isStaleWrite(error)) {
            setStale(true);
            onReload();
          } else {
            show({ tone: 'danger', title: problem.text(error, { fallback: t('floor.arrange.saveFailed') }) });
          }
        },
      },
    );
  };

  const loadTheirs = () => {
    setBase(plan);
    setDraft(draftOf(plan));
    setStale(false);
    setSelection(null);
  };

  const cancel = () => {
    setDraft(draftOf(base));
    setSelection(null);
    setRefusal(null);
  };

  const floors = Array.from({ length: FLOOR_MAX - FLOOR_MIN + 1 }, (_, index) => FLOOR_MIN + index);
  const inUse = new Set(plan.floorsInUse);
  const selectedFixture = selection?.kind === 'fixture' ? draft.fixtures[selection.index] : undefined;
  const gridStyle = { '--arrange-columns': draft.columns, '--arrange-rows': draft.rows } as CSSProperties;

  return (
    <Page
      width="wide"
      title={t('floor.arrange.title')}
      description={t('floor.arrange.intro')}
      actions={
        <Button asChild variant="ghost">
          <Link to="/v/$venueSlug" params={{ venueSlug: venue.slug }}>
            {t('floor.arrange.backToFloor')}
          </Link>
        </Button>
      }
    >
      <Toolbar label={t('floor.arrange.toolbar')}>
        <label className="console-arrange__floor-pick">
          <span className="console-subtle">{t('floor.arrange.floorLabel')}</span>
          <Select
            aria-label={t('floor.arrange.floorLabel')}
            value={String(floor)}
            onValueChange={(value) => {
              if (dirty && !window.confirm(t('floor.arrange.discardConfirm'))) return;
              onFloorChange(Number(value));
            }}
            options={floors.map((level) => ({
              value: String(level),
              label: `${level === 0 ? t('floor.arrange.ground') : t('floor.arrange.level', { level })}${inUse.has(level) ? '' : ` ${t('floor.arrange.unused')}`}`,
            }))}
          />
        </label>
        <SegmentedControl<Layer>
          label={t('floor.arrange.layer.label')}
          value={layer}
          onValueChange={(next) => {
            setLayer(next);
            setSelection(null);
          }}
          options={[
            { value: 'stations', label: t('floor.arrange.layer.stations') },
            { value: 'zones', label: t('floor.arrange.layer.zones') },
            { value: 'fixtures', label: t('floor.arrange.layer.fixtures') },
          ]}
        />
        <ToolbarSpring />
        <SizeStepper label={t('floor.arrange.columns')} value={draft.columns} onStep={(delta) => resize(draft.columns + delta, draft.rows)} />
        <SizeStepper label={t('floor.arrange.rows')} value={draft.rows} onStep={(delta) => resize(draft.columns, draft.rows + delta)} />
      </Toolbar>

      {stale ? (
        <Notice
          tone="warn"
          role="alert"
          title={t('floor.arrange.staleTitle')}
          action={
            <Button size="sm" variant="secondary" onClick={loadTheirs}>
              {t('floor.arrange.loadTheirs')}
            </Button>
          }
        >
          {t('floor.arrange.stale')}
        </Notice>
      ) : null}
      {refusal !== null ? (
        <Notice tone="warn" role="status">
          {refusal}
        </Notice>
      ) : null}

      <div className="console-arrange">
        <div className="console-arrange__canvas-wrap">
          {/* The grid is one pointer surface (a drop or a click reads its square from the pointer); every item on it is
              a real button with its own keyboard moves, and Escape here lets go of the selection. */}
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
          <div
            ref={gridRef}
            className={cx('console-arrange__grid', `console-arrange__grid--${layer}`)}
            onKeyDown={(event) => {
              if (event.key === 'Escape') setSelection(null);
            }}
            style={gridStyle}
            role="application"
            aria-label={t('floor.arrange.gridLabel', { columns: draft.columns, rows: draft.rows })}
            aria-describedby="console-arrange-help"
            onDragOver={(event) => {
              if (layer === 'stations' || layer === 'fixtures') event.preventDefault();
            }}
            onDrop={onDrop}
            onPointerDown={onGridPointerDown}
            onPointerMove={onGridPointerMove}
            onPointerUp={onGridPointerUp}
            onClick={(event) => {
              if (event.target !== gridRef.current) return;
              if (selection?.kind === 'station' && layer === 'stations') {
                const cell = cellAt(event.clientX, event.clientY);
                if (cell !== undefined) placeStation(selection.stationId, cell.x, cell.y);
              }
            }}
          >
            {draft.zones.map((zone) => {
              const known = zoneById.get(zone.zoneId);
              const selected = selectedZoneId === zone.zoneId;
              return (
                <button
                  key={`z-${zone.zoneId}`}
                  ref={(node) => {
                    if (node === null) itemRefs.current.delete(`z:${zone.zoneId}`);
                    else itemRefs.current.set(`z:${zone.zoneId}`, node);
                  }}
                  type="button"
                  tabIndex={layer === 'zones' ? 0 : -1}
                  className={cx('console-arrange__zone', selected && 'console-arrange__zone--selected')}
                  style={{ ...gridPlace(zone), '--zone-color': known === undefined ? undefined : tokenColor(known.colorToken) } as CSSProperties}
                  aria-label={t('floor.arrange.zoneAria', { name: known?.name ?? t('floor.arrange.unknownZone'), x: zone.x + 1, y: zone.y + 1, w: zone.w, h: zone.h })}
                  aria-pressed={selected}
                  onClick={() => setSelection({ kind: 'zone', zoneId: zone.zoneId })}
                  onKeyDown={(event) => onZoneKey(event, zone.zoneId)}
                >
                  <span className="console-arrange__zone-name">{known?.name ?? ''}</span>
                </button>
              );
            })}
            {paint !== null ? (
              <span
                className="console-arrange__paint"
                style={gridPlace({ x: Math.min(paint.x0, paint.x1), y: Math.min(paint.y0, paint.y1), w: Math.abs(paint.x1 - paint.x0) + 1, h: Math.abs(paint.y1 - paint.y0) + 1 })}
                aria-hidden="true"
              />
            ) : null}
            {draft.fixtures.map((fixture, index) => {
              const selected = selection?.kind === 'fixture' && selection.index === index;
              return (
                <button
                  key={`f-${index}`}
                  ref={(node) => {
                    if (node === null) itemRefs.current.delete(`f:${index}`);
                    else itemRefs.current.set(`f:${index}`, node);
                  }}
                  type="button"
                  draggable={layer === 'fixtures'}
                  tabIndex={layer === 'fixtures' ? 0 : -1}
                  className={cx('console-arrange__fixture', `console-arrange__fixture--${fixture.kind}`, selected && 'console-arrange__fixture--selected')}
                  style={gridPlace(fixture)}
                  aria-label={t('floor.arrange.fixtureAria', { kind: t(`floor.fixture.${fixture.kind}`), label: fixture.label ?? '', x: fixture.x + 1, y: fixture.y + 1, w: fixture.w, h: fixture.h })}
                  aria-pressed={selected}
                  onDragStart={(event) => onFixtureDragStart(event, index)}
                  onClick={() => setSelection({ kind: 'fixture', index })}
                  onKeyDown={(event) => onFixtureKey(event, index)}
                >
                  <span className="console-arrange__fixture-label">{fixture.label ?? t(`floor.fixture.${fixture.kind}`)}</span>
                </button>
              );
            })}
            {draft.stations.map((station) => {
              const known = byId.get(station.stationId);
              const selected = selection?.kind === 'station' && selection.stationId === station.stationId;
              return (
                <button
                  key={`s-${station.stationId}`}
                  ref={(node) => {
                    if (node === null) itemRefs.current.delete(`s:${station.stationId}`);
                    else itemRefs.current.set(`s:${station.stationId}`, node);
                  }}
                  type="button"
                  draggable={layer === 'stations'}
                  tabIndex={layer === 'stations' ? 0 : -1}
                  className={cx('console-arrange__station', selected && 'console-arrange__station--selected')}
                  style={gridPlace(station)}
                  aria-label={t('floor.arrange.stationAria', { name: stationName(station.stationId), x: station.x + 1, y: station.y + 1 })}
                  aria-pressed={selected}
                  onDragStart={(event) => onDragStart(event, station.stationId)}
                  onClick={() => setSelection({ kind: 'station', stationId: station.stationId })}
                  onKeyDown={(event) => onStationKey(event, station.stationId)}
                >
                  {known?.number ?? '?'}
                </button>
              );
            })}
          </div>
        </div>

        <aside className="console-arrange__panel" aria-label={t('floor.arrange.panelLabel')}>
          <p id="console-arrange-help" className="console-subtle console-arrange__help">
            {t(`floor.arrange.help.${layer}`)}
          </p>

          {layer === 'stations' ? (
            <section className="console-arrange__section" aria-label={t('floor.arrange.tray')}>
              <header className="console-arrange__section-head">
                <h2 className="console-arrange__section-title">{t('floor.arrange.tray')}</h2>
                <Badge>{tray.length}</Badge>
              </header>
              {tray.length === 0 ? (
                <p className="console-subtle">{t('floor.arrange.trayEmpty')}</p>
              ) : (
                <ul className="console-arrange__tray">
                  {tray.map((station) => (
                    <li key={station.stationId}>
                      <button
                        type="button"
                        draggable
                        className="console-arrange__tray-item"
                        onDragStart={(event) => onDragStart(event, station.stationId)}
                        onClick={() => placeFromTray(station.stationId)}
                        aria-label={t('floor.arrange.placeAria', { name: station.name ?? t('floor.tile.numbered', { number: station.number }) })}
                      >
                        <span className="console-arrange__tray-number">{station.number}</span>
                        {station.name ?? ''}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {plan.stationsElsewhere.length > 0 ? <p className="console-subtle">{t('floor.arrange.elsewhere', { count: plan.stationsElsewhere.length })}</p> : null}
              <Button size="sm" variant="secondary" icon={<Icon name="grid" />} onClick={autoLayout}>
                {t('floor.arrange.autoLayout')}
              </Button>
              {selection?.kind === 'station' ? (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setStations(trayStation(draft.stations, selection.stationId));
                    setSelection(null);
                  }}
                >
                  {t('floor.arrange.toTray', { name: stationName(selection.stationId) })}
                </Button>
              ) : null}
            </section>
          ) : null}

          {layer === 'zones' ? (
            <section className="console-arrange__section" aria-label={t('floor.arrange.layer.zones')}>
              {zonesLoading ? (
                <p className="console-subtle" role="status">
                  {t('floor.arrange.zonesLoading')}
                </p>
              ) : zonesError ? (
                <Notice
                  tone="danger"
                  role="alert"
                  action={
                    <Button size="sm" variant="secondary" onClick={onReloadZones}>
                      {t('floor.arrange.zonesRetry')}
                    </Button>
                  }
                >
                  {t('floor.arrange.zonesUnavailable')}
                </Notice>
              ) : zones.length === 0 ? (
                <p className="console-subtle">{t('floor.arrange.noZones')}</p>
              ) : (
                <ul className="console-arrange__zone-list">
                  {zones.map((zone) => {
                    const drawn = draft.zones.some((rect) => rect.zoneId === zone.id);
                    return (
                      <li key={zone.id}>
                        <button
                          type="button"
                          className={cx('console-arrange__zone-pick', selectedZoneId === zone.id && 'console-arrange__zone-pick--selected')}
                          style={{ '--zone-color': tokenColor(zone.colorToken) } as CSSProperties}
                          aria-pressed={selectedZoneId === zone.id}
                          onClick={() => drawZone(zone.id)}
                        >
                          <span className="console-arrange__swatch" aria-hidden="true" />
                          {zone.name}
                          <span className="console-subtle">{drawn ? t('floor.arrange.zoneDrawn') : t('floor.arrange.zoneNotDrawn')}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          ) : null}

          {layer === 'fixtures' ? (
            <section className="console-arrange__section" aria-label={t('floor.arrange.layer.fixtures')}>
              <div className="console-arrange__add">
                <Select
                  aria-label={t('floor.arrange.fixtureKind')}
                  value={newFixture}
                  onValueChange={(value) => setNewFixture(value as FixtureKind)}
                  options={FIXTURE_KINDS.map((kind) => ({ value: kind, label: t(`floor.fixture.${kind}`) }))}
                />
                <Button size="sm" variant="secondary" icon={<Icon name="plus" />} onClick={addFixture}>
                  {t('floor.arrange.addFixture')}
                </Button>
              </div>
              <p className="console-subtle">{t('floor.arrange.fixtureCount', { count: draft.fixtures.length, max: MAX_FIXTURES_PER_FLOOR })}</p>
              {selectedFixture !== undefined && selection?.kind === 'fixture' ? (
                <label className="console-arrange__label-field">
                  <span>{t('floor.arrange.fixtureLabel')}</span>
                  <input
                    className="clav-input"
                    value={selectedFixture.label ?? ''}
                    maxLength={40}
                    onChange={(event) => setFixtureLabel(selection.index, event.target.value)}
                  />
                </label>
              ) : null}
            </section>
          ) : null}

          {problems.length > 0 ? (
            <section className="console-arrange__section" aria-label={t('floor.arrange.problems')}>
              <h2 className="console-arrange__section-title">{t('floor.arrange.problems')}</h2>
              <ul className="console-arrange__problems">
                {problems.map((problem, index) => (
                  <li key={`${problem.field}-${index}`}>{problem.message}</li>
                ))}
              </ul>
            </section>
          ) : null}

          <div className="console-arrange__actions">
            <Button variant="primary" onClick={onSave} disabled={!dirty || problems.length > 0 || save.isPending} busy={save.isPending}>
              {t('floor.arrange.save')}
            </Button>
            <Button variant="ghost" onClick={cancel} disabled={!dirty}>
              {t('floor.arrange.cancel')}
            </Button>
          </div>
        </aside>
      </div>
    </Page>
  );
}

function SizeStepper({ label, value, onStep }: { readonly label: string; readonly value: number; readonly onStep: (delta: number) => void }): ReactNode {
  const { t } = useTranslation('console');
  return (
    <span className="console-arrange__size" role="group" aria-label={label}>
      <span className="console-subtle">{label}</span>
      <Button size="sm" variant="ghost" label={t('floor.arrange.fewer', { what: label })} onClick={() => onStep(-1)}>
        −
      </Button>
      <span className="console-num" aria-live="polite">
        {value}
      </span>
      <Button size="sm" variant="ghost" label={t('floor.arrange.more', { what: label })} onClick={() => onStep(1)}>
        +
      </Button>
    </span>
  );
}

/** The plan's query key, for a test or a caller that wants to seed it. */
export const floorPlanKey = floorKeys.plan;
